import { LAYOUTS, LAYOUT_ORDER, GAMEPAD_HINT } from '../core/input';
import { devices, setDevice, type DeviceKind } from '../core/control-config';
import type { RaceResult } from '../core/types';
import './controls.css';

export interface ControlPlayer {
  playerIndex: number;
  characterName: string;
}

export interface Overlay {
  close(): void;
  element: HTMLElement;
}

export interface PauseOptions {
  players: ControlPlayer[];
  onResume: () => void;
  onRestart: () => void;
  onMenu: () => void;
  musicOn?: boolean;
  onToggleMusic?: () => boolean;
  sfxOn?: boolean;
  onToggleSfx?: () => boolean;
}

export interface SoundApi {
  musicEnabled: boolean;
  sfxEnabled: boolean;
  muted: boolean;
  toggleMusic: () => boolean;
  toggleSfx: () => boolean;
  toggleMute: () => boolean;
}

export interface SettingsOptions {
  sound?: SoundApi;
  players?: ControlPlayer[];
  onDone?: () => void;
}

export interface SetupOptions {
  players: ControlPlayer[];
  onDone: () => void;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

/** A játékos által használt eszköz rövid leírása (tipp / HUD). */
export const deviceLine = (playerIndex: number): string => {
  const d = devices[playerIndex];
  return d.kind === 'gamepad'
    ? `Gamepad ${d.pad + 1} — ${GAMEPAD_HINT}`
    : `Billentyűzet · ${LAYOUTS[d.layout].name} — ${LAYOUTS[d.layout].keys}`;
};

const padCount = (): number => {
  try {
    return (navigator.getGamepads?.() ?? []).filter((p) => p !== null).length;
  } catch {
    return 0;
  }
};

/** Játékosonkénti eszköz-választó sorok (billentyűzet-kiosztás vagy gamepad). */
const deviceRows = (players: ControlPlayer[], rerender: () => void): HTMLElement => {
  const list = el('div', 'csf-pause__players');
  const connected = padCount();

  for (const p of players) {
    const d = devices[p.playerIndex];
    const row = el('div', 'csf-pause__row');

    const who = el('div', 'csf-pause__who');
    who.appendChild(el('span', 'csf-pause__badge', `P${p.playerIndex + 1}`));
    if (p.characterName) who.appendChild(el('span', 'csf-pause__name', p.characterName));
    row.appendChild(who);

    // eszköz típusa
    const kinds = el('div', 'csf-pause__choices');
    const kindBtn = (kind: DeviceKind, label: string): void => {
      const btn = el('button', 'csf-pause__choice', label);
      btn.type = 'button';
      if (d.kind === kind) btn.classList.add('is-active');
      btn.addEventListener('click', () => {
        if (d.kind === kind) return;
        setDevice(p.playerIndex, { kind });
        rerender();
      });
      kinds.appendChild(btn);
    };
    kindBtn('keyboard', 'Billentyűzet');
    kindBtn('gamepad', 'Gamepad');
    row.appendChild(kinds);

    // opciók a választott eszközhöz
    const options = el('div', 'csf-pause__choices');
    if (d.kind === 'keyboard') {
      for (const layout of LAYOUT_ORDER) {
        const btn = el('button', 'csf-pause__choice', LAYOUTS[layout].name);
        btn.type = 'button';
        if (d.layout === layout) btn.classList.add('is-active');
        btn.addEventListener('click', () => {
          setDevice(p.playerIndex, { layout });
          rerender();
        });
        options.appendChild(btn);
      }
    } else {
      for (let pad = 0; pad < 4; pad++) {
        const btn = el('button', 'csf-pause__choice', `Pad ${pad + 1}${pad < connected ? ' •' : ''}`);
        btn.type = 'button';
        if (d.pad === pad) btn.classList.add('is-active');
        btn.addEventListener('click', () => {
          setDevice(p.playerIndex, { pad });
          rerender();
        });
        options.appendChild(btn);
      }
    }
    row.appendChild(options);
    row.appendChild(el('div', 'csf-pause__keys', deviceLine(p.playerIndex)));
    list.appendChild(row);
  }
  return list;
};

const makeOverlay = (root: HTMLElement, className: string): { wrap: HTMLElement; card: HTMLElement } => {
  const wrap = el('div', className);
  const card = el('div', 'csf-pause__card');
  wrap.appendChild(card);
  root.appendChild(wrap);
  return { wrap, card };
};

/** Induláskor megjelenő irányítás-kártya; néhány másodperc után eltűnik. */
export function showStartHint(root: HTMLElement, players: ControlPlayer[], durationMs = 7500): Overlay {
  const wrap = el('div', 'csf-hint');
  const card = el('div', 'csf-hint__card');

  card.appendChild(el('div', 'csf-hint__title', 'ÍGY IRÁNYÍTS'));
  for (const p of players) {
    const line = el('div', 'csf-hint__line');
    line.appendChild(el('span', 'csf-hint__badge', `P${p.playerIndex + 1}`));
    line.appendChild(el('span', 'csf-hint__who', p.characterName));
    line.appendChild(el('span', 'csf-hint__keys', deviceLine(p.playerIndex)));
    card.appendChild(line);
  }
  card.appendChild(el('div', 'csf-hint__foot', 'ESC — szünet és vezérlés-beállítások'));

  wrap.appendChild(card);
  root.appendChild(wrap);

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    wrap.classList.add('csf-hint--out');
    window.setTimeout(() => wrap.remove(), 320);
  };
  const timer = window.setTimeout(close, durationMs);

  return {
    element: wrap,
    close: () => {
      window.clearTimeout(timer);
      close();
    },
  };
}

/** Beállítások a főképernyőről: hang + játékosonkénti vezérlés. */
export function showSettings(root: HTMLElement, opts: SettingsOptions): Overlay {
  const { wrap, card } = makeOverlay(root, 'csf-pause');
  const players =
    opts.players ?? [0, 1, 2, 3].map((i) => ({ playerIndex: i, characterName: '' }));

  const head = el('div', 'csf-pause__head');
  head.appendChild(el('div', 'csf-pause__title', 'BEÁLLÍTÁSOK'));
  head.appendChild(el('div', 'csf-pause__sub', 'Hang és vezérlés — verseny előtt is'));
  card.appendChild(head);

  if (opts.sound) {
    const sound = opts.sound;
    card.appendChild(el('div', 'csf-settings__label', 'HANG'));

    const row = el('div', 'csf-settings__toggles');
    const makeToggle = (label: string, read: () => boolean, toggle: () => boolean): void => {
      const btn = el('button', 'mode-toggle', '');
      btn.type = 'button';
      const refresh = (): void => {
        const on = read();
        btn.textContent = `${label}: ${on ? 'BE' : 'KI'}`;
        btn.classList.toggle('is-active', on);
      };
      btn.addEventListener('click', () => {
        toggle();
        refresh();
      });
      refresh();
      row.appendChild(btn);
    };

    makeToggle('ZENE', () => sound.musicEnabled, () => sound.toggleMusic());
    makeToggle('HANGEFFEKTEK', () => sound.sfxEnabled, () => sound.toggleSfx());
    const mute = el('button', 'mode-toggle', '');
    mute.type = 'button';
    const refreshMute = (): void => {
      mute.textContent = `NÉMÍTÁS: ${sound.muted ? 'BE' : 'KI'}`;
      mute.classList.toggle('is-danger', sound.muted);
    };
    mute.addEventListener('click', () => {
      sound.toggleMute();
      refreshMute();
    });
    refreshMute();
    row.appendChild(mute);
    card.appendChild(row);
  }

  card.appendChild(el('div', 'csf-settings__label', 'VEZÉRLÉS'));
  let list = deviceRows(players, rerender);
  card.appendChild(list);

  function rerender(): void {
    const next = deviceRows(players, rerender);
    list.replaceWith(next);
    list = next;
  }

  const buttons = el('div', 'csf-pause__buttons');
  let closed = false;
  let onKey: (e: KeyboardEvent) => void = () => undefined;
  const close = (): void => {
    if (closed) return;
    closed = true;
    window.removeEventListener('keydown', onKey, true);
    wrap.remove();
    opts.onDone?.();
  };

  const done = el('button', 'csf-pause__btn csf-pause__btn--primary', 'KÉSZ');
  done.type = 'button';
  done.addEventListener('click', close);
  buttons.appendChild(done);
  card.appendChild(buttons);
  card.appendChild(el('div', 'csf-pause__foot', 'Enter / Esc — vissza · a beállítás azonnal érvényes'));

  onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Enter' || e.code === 'Escape' || e.code === 'NumpadEnter') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };
  window.addEventListener('keydown', onKey, true);

  return { element: wrap, close };
}

/** Teljes képernyős vezérlés-beállítás (menüből, indulás előtt). */
export function showControlSetup(root: HTMLElement, opts: SetupOptions): Overlay {
  const { wrap, card } = makeOverlay(root, 'csf-pause');

  const head = el('div', 'csf-pause__head');
  head.appendChild(el('div', 'csf-pause__title', 'VEZÉRLÉS'));
  head.appendChild(el('div', 'csf-pause__sub', 'Válaszd ki, ki mivel játszik'));
  card.appendChild(head);

  let list = deviceRows(opts.players, rerender);
  card.appendChild(list);

  function rerender(): void {
    const next = deviceRows(opts.players, rerender);
    list.replaceWith(next);
    list = next;
  }

  const buttons = el('div', 'csf-pause__buttons');
  let closed = false;
  let onKey: (e: KeyboardEvent) => void = () => undefined;
  const close = (): void => {
    if (closed) return;
    closed = true;
    window.removeEventListener('keydown', onKey, true);
    wrap.remove();
  };

  const done = el('button', 'csf-pause__btn csf-pause__btn--primary', 'KÉSZ');
  done.type = 'button';
  done.addEventListener('click', () => {
    close();
    opts.onDone();
  });
  buttons.appendChild(done);
  card.appendChild(buttons);
  card.appendChild(el('div', 'csf-pause__foot', 'Enter / Esc — vissza · a beállítás azonnal érvényes'));

  onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Enter' || e.code === 'Escape' || e.code === 'NumpadEnter') {
      e.preventDefault();
      e.stopPropagation();
      close();
      opts.onDone();
    }
  };
  window.addEventListener('keydown', onKey, true);

  return { element: wrap, close };
}

/** Battle végeredmény: győztes csapat, életek, játékosok. */
export function showBattleResult(
  root: HTMLElement,
  result: RaceResult,
  handlers: { onRestart: () => void; onMenu: () => void },
): Overlay {
  const { wrap, card } = makeOverlay(root, 'csf-pause');
  const battle = result.battle;
  if (!battle) {
    wrap.remove();
    return { element: wrap, close: () => undefined };
  }

  const head = el('div', 'csf-pause__head');
  head.appendChild(el('div', 'csf-pause__title', 'CSATA VÉGE'));
  const winner = battle.winningTeam;
  const winnerText = winner < 0 ? 'DÖNTETLEN' : `${battle.teams[winner].name} CSAPAT GYŐZÖTT`;
  const winnerEl = el('div', 'csf-battle-winner', winnerText);
  winnerEl.style.color = winner < 0 ? '#f4f1e8' : battle.teams[winner].color;
  head.appendChild(winnerEl);
  card.appendChild(head);

  const score = el('div', 'csf-battle-score');
  for (const team of battle.teams) {
    const box = el('div', 'csf-battle-team');
    box.style.borderColor = team.color;
    const name = el('span', 'csf-battle-team-name', team.name);
    name.style.color = team.color;
    const lives = el('span', 'csf-battle-team-lives', `${team.lives} élet`);
    box.append(name, lives);
    score.appendChild(box);
  }
  card.appendChild(score);

  const list = el('div', 'csf-battle-players');
  const sorted = [...result.entries].sort((a, b) => (a.team ?? 0) - (b.team ?? 0) || (b.lives ?? 0) - (a.lives ?? 0));
  for (const entry of sorted) {
    const row = el('div', 'csf-battle-row');
    const dot = el('span', 'csf-battle-dot');
    dot.style.background = entry.color;
    const name = el('span', 'csf-battle-name', entry.short);
    const lives = el('span', 'csf-battle-lives', '●'.repeat(entry.lives ?? 0) || '—');
    lives.style.color = entry.color;
    const tag = el('span', 'csf-battle-tag', entry.isPlayer ? 'EMBER' : 'AI');
    row.append(dot, name, lives, tag);
    list.appendChild(row);
  }
  card.appendChild(list);

  const buttons = el('div', 'csf-pause__buttons');
  let closed = false;
  let onKey: (e: KeyboardEvent) => void = () => undefined;
  const close = (): void => {
    if (closed) return;
    closed = true;
    window.removeEventListener('keydown', onKey, true);
    wrap.remove();
  };

  const restart = el('button', 'csf-pause__btn csf-pause__btn--primary', 'ÚJRA');
  restart.type = 'button';
  restart.addEventListener('click', () => {
    close();
    handlers.onRestart();
  });
  const menu = el('button', 'csf-pause__btn', 'MENÜ');
  menu.type = 'button';
  menu.addEventListener('click', () => {
    close();
    handlers.onMenu();
  });
  buttons.append(restart, menu);
  card.appendChild(buttons);
  card.appendChild(el('div', 'csf-pause__foot', 'Enter = újra · Esc = menü'));

  onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      e.preventDefault();
      e.stopPropagation();
      close();
      handlers.onRestart();
    } else if (e.code === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      handlers.onMenu();
    }
  };
  window.addEventListener('keydown', onKey, true);

  return { element: wrap, close };
}

export function showPause(root: HTMLElement, opts: PauseOptions): Overlay {
  const { wrap, card } = makeOverlay(root, 'csf-pause');

  const head = el('div', 'csf-pause__head');
  head.appendChild(el('div', 'csf-pause__title', 'SZÜNET'));
  head.appendChild(el('div', 'csf-pause__sub', 'Vezérlés játékosonként'));
  card.appendChild(head);

  let list = deviceRows(opts.players, rerender);
  card.appendChild(list);

  function rerender(): void {
    const next = deviceRows(opts.players, rerender);
    list.replaceWith(next);
    list = next;
  }

  const buttons = el('div', 'csf-pause__buttons');
  let closed = false;
  let onKey: (e: KeyboardEvent) => void = () => undefined;
  const close = (): void => {
    if (closed) return;
    closed = true;
    window.removeEventListener('keydown', onKey, true);
    wrap.remove();
  };

  const resume = el('button', 'csf-pause__btn csf-pause__btn--primary', 'FOLYTATÁS');
  resume.type = 'button';
  resume.addEventListener('click', () => {
    close();
    opts.onResume();
  });

  const restart = el('button', 'csf-pause__btn', 'ÚJRA');
  restart.type = 'button';
  restart.addEventListener('click', () => {
    close();
    opts.onRestart();
  });

  const menu = el('button', 'csf-pause__btn', 'MENÜ');
  menu.type = 'button';
  menu.addEventListener('click', () => {
    close();
    opts.onMenu();
  });

  const music = el('button', 'csf-pause__btn', `ZENE: ${opts.musicOn ? 'BE' : 'KI'}`);
  music.type = 'button';
  music.addEventListener('click', () => {
    if (!opts.onToggleMusic) return;
    const on = opts.onToggleMusic();
    music.textContent = `ZENE: ${on ? 'BE' : 'KI'}`;
  });

  const sfx = el('button', 'csf-pause__btn', `HANGEFFEKT: ${opts.sfxOn ? 'BE' : 'KI'}`);
  sfx.type = 'button';
  sfx.addEventListener('click', () => {
    if (!opts.onToggleSfx) return;
    const on = opts.onToggleSfx();
    sfx.textContent = `HANGEFFEKT: ${on ? 'BE' : 'KI'}`;
  });

  buttons.append(resume, music, sfx, restart, menu);
  card.appendChild(buttons);
  card.appendChild(el('div', 'csf-pause__foot', 'Enter / Esc — folytatás · a beállítás azonnal életbe lép'));

  onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Enter' || e.code === 'Escape' || e.code === 'NumpadEnter') {
      e.preventDefault();
      e.stopPropagation();
      close();
      opts.onResume();
    }
  };
  window.addEventListener('keydown', onKey, true);

  return { element: wrap, close };
}
